import { jsx as _jsx } from "react/jsx-runtime";
import { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FAL_EXCLAMATION_CIRCLE, FAR_ANGLE_DOWN, FAR_ANGLE_UP, FAR_UNIVERSITY } from '../../../../const/iconProps';
import { PINLESS_DEBIT_CARD } from '../../../../const/paymentMedium';
import { paymentTypes } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import { isPayLaterProduct, isRevolvingCreditProduct } from '../../../../helpers';
import isDCHEligible from '../../../../helpers/isDCHEligible';
import shouldEnableManagePaymentMethods from '../../../../helpers/shouldEnableManagePaymentMethods';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { getPaymentOptionAndMethod } from '../../../../machines/helpers';
import { PAYMENT_METHOD_INPUT_NAME } from '../../../../machines/oneTimePayment/paymentMethodConfig';
import { t } from '../../../../strings/i18n';
import SelectWithFocus from '../../../../ui/atoms/SelectWithFocus';
import { useField } from '../../../../xform';
import { SelectWrapper } from '../subcomponents';
import { getPaymentMethodOptions, getPaymentOption } from './helpers';
const PaymentMethodSelect = ({ id, disabled = false }) => {
    var _a;
    const { sendToMainMachine, featureFlags: { isManagePaymentMethodsEnabled, isPLAddPaymentMethodsEnabled } } = useContext(MainContext);
    const [apiMachine] = useApiMachine();
    const [paymentType, paymentFlowMachine, sendToPaymentFlowMachine] = usePaymentFlowMachine();
    const { accountSummaryQueryResult, alternatePaymentMethodsQueryResult, paymentMethodsQueryResult, featureFlags } = apiMachine.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: paymentMethodData } = paymentMethodsQueryResult;
    const { data: alternatePaymentMethodsData } = alternatePaymentMethodsQueryResult;
    const paymentMethodField = useField(PAYMENT_METHOD_INPUT_NAME);
    const { billing, product } = accountSummary;
    const isAutopayFlow = paymentType === paymentTypes.AUTOPAY;
    const isPayLaterProductForOneTimeOrPayoffPayment = isPayLaterProduct(product.type) && !isAutopayFlow;
    const displayManagePaymentMethods = shouldEnableManagePaymentMethods(product.type, isManagePaymentMethodsEnabled);
    const alternatePaymentMethods = (_a = alternatePaymentMethodsData === null || alternatePaymentMethodsData === void 0 ? void 0 : alternatePaymentMethodsData.paymentMethods) !== null && _a !== void 0 ? _a : [];
    const registeredPaymentMethods = isPLAddPaymentMethodsEnabled || !isPayLaterProductForOneTimeOrPayoffPayment
        ? paymentMethodData.paymentMethods
        : [];
    const [, paymentMethod] = getPaymentOptionAndMethod(paymentFlowMachine.context.form, alternatePaymentMethodsData, paymentMethodData);
    const paymentMethods = [
        ...alternatePaymentMethods,
        ...registeredPaymentMethods
    ].filter((method) => isAutopayFlow && isRevolvingCreditProduct(product.type)
        ? method.paymentMedium !== 'PINLESS_DEBIT_CARD'
        : method);
    const setShowAddPaymentMethod = () => {
        sendToMainMachine({
            type: 'GO_TO_PAYMENT_METHOD_FLOW'
        });
    };
    const methodOptions = getPaymentMethodOptions(paymentMethods, setShowAddPaymentMethod, displayManagePaymentMethods);
    const paymentMethodPlaceholder = paymentMethodField.value
        ? t('makePayment.paymentMethod')
        : t('label.paymentMethodPlaceholder');
    const onChange = (option) => {
        paymentMethodField.setValue(option.value);
        paymentMethodField.blur();
        sendToPaymentFlowMachine({
            type: 'SET_CURRENT_DATE'
        });
    };
    const isDebitCardSelected = (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.paymentMedium) === PINLESS_DEBIT_CARD;
    const isDCHDebitCardSelected = isDCHEligible(billing, product.type, featureFlags) && isDebitCardSelected;
    const helperText = isDCHDebitCardSelected
        ? t('label.oneTimePaymentPaymentMethodHelpText.dchDebitCard')
        : '';
    return (_jsx(SelectWrapper, Object.assign({ "data-scroll": PAYMENT_METHOD_INPUT_NAME, horizontal: "medium" }, { children: _jsx(SelectWithFocus, { autoAdjust: true, dataCy: "payment-method-field", defaultOption: !paymentMethodField.isDisabled &&
                getPaymentOption(methodOptions, paymentMethodField.value), error: paymentMethodField.error, errorIcon: _jsx(FontAwesomeIcon, { icon: FAL_EXCLAMATION_CIRCLE, size: "1x" }), helpText: helperText, id: id, isDisabled: paymentMethodField.isDisabled || disabled, leftIcon: _jsx(FontAwesomeIcon, { icon: FAR_UNIVERSITY }), name: id, options: methodOptions, placeholder: paymentMethodPlaceholder, required: true, rightIconClosed: _jsx(FontAwesomeIcon, { icon: FAR_ANGLE_DOWN, size: "lg" }), rightIconOpen: _jsx(FontAwesomeIcon, { icon: FAR_ANGLE_UP, size: "lg" }), tabIndex: paymentMethodField.isDisabled ? -1 : 0, onChange: onChange }) })));
};
export default PaymentMethodSelect;
